import React, { useState } from "react";
import {
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import GetAppIcon from "@mui/icons-material/GetApp";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { format } from "date-fns";

// ─── number → Indian words ────────────────────────────────────────────────────
export function convertNumberToWords(amount: number): string {
  if (amount === 0) return "Zero Rupees Only";
  const sd = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const dd = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tm = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const lt1k = (n: number): string => {
    let s = "";
    if (n >= 100) { s += sd[Math.floor(n / 100)] + " Hundred "; n %= 100; }
    if (n >= 10 && n < 20) s += dd[n - 10] + " ";
    else if (n >= 20) s += tm[Math.floor(n / 10)] + " " + sd[n % 10] + " ";
    else if (n > 0) s += sd[n] + " ";
    return s;
  };
  let rv = Math.floor(amount);
  const pv = Math.round((amount - rv) * 100);
  let rs = "";
  if (rv >= 10000000) { rs += lt1k(Math.floor(rv / 10000000)) + "Crore "; rv %= 10000000; }
  if (rv >= 100000) { rs += lt1k(Math.floor(rv / 100000)) + "Lakh "; rv %= 100000; }
  if (rv >= 1000) { rs += lt1k(Math.floor(rv / 1000)) + "Thousand "; rv %= 1000; }
  if (rv > 0) rs += lt1k(rv);
  let result = rs.trim() + " Rupees";
  if (pv > 0) result += " and " + lt1k(pv).trim() + " Paisa";
  return result + " Only";
}

// ─── types ────────────────────────────────────────────────────────────────────
interface TransportInvoicePreviewProps {
  open: boolean;
  onClose: () => void;
  data: any;
  handleClickOpenSendEmail?: (open: boolean, itemData: any) => void;
  handleClickOpenSendWhatsapp?: (open: boolean, itemData: any) => void;
  itemData?: any;
  template?: string;
}

// ─── main component ───────────────────────────────────────────────────────────
export default function TransportInvoicePreview(props: TransportInvoicePreviewProps) {
  const { onClose, data, handleClickOpenSendEmail, handleClickOpenSendWhatsapp, itemData, template } = props;
  const [zoom, setZoom] = useState<number>(100);
  const [selectedCopy, setSelectedCopy] = useState<string>("Both");
  const [isDownloading, setIsDownloading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!data) return null;

  const header = data.header || data.Header || {};
  const details = data.detailCollection || data.DetailCollection || [];
  const taxList = data.taxSummaryCollection || data.TaxSummaryCollection || [];
  const companyInfo = data.companyInfo || data.CompanyInfo || {};

  const fmtDate = (v: any) => {
    if (!v) return "";
    try { const d = new Date(v); return isNaN(d.getTime()) ? v : format(d, "dd-MM-yyyy"); }
    catch { return v; }
  };

  const invoiceDate = fmtDate(header.saleDate || header.SaleDate);
  const dueDate = fmtDate(header.dueOnDate || header.DueOnDate);

  const totalActQty = details.reduce((s: number, i: any) => s + Number(i.actualQuantity || i.ActualQuantity || 0), 0);
  const totalBillQty = details.reduce((s: number, i: any) => s + Number(i.billQuantity || i.BillQuantity || 0), 0);
  const totalAmt = details.reduce((s: number, i: any) => s + Number(i.finalAmount || i.FinalAmount || 0) + Number(i.waitingCharges || i.WaitingCharges || 0), 0);
  const totalTaxableA = taxList.reduce((s: number, i: any) => s + Number(i.totalTaxableAmount || i.TotalTaxableAmount || 0), 0);
  const totalCGST = taxList.reduce((s: number, i: any) => s + Number(i.totalCentralTaxAmount || i.TotalCentralTaxAmount || 0), 0);
  const totalSGST = taxList.reduce((s: number, i: any) => s + Number(i.totalStateTaxAmount || i.TotalStateTaxAmount || 0), 0);
  const totalIGST = taxList.reduce((s: number, i: any) => s + Number(i.totalIntegratedTaxAmount || i.TotalIntegratedTaxAmount || 0), 0);

  const first = taxList[0] || {};
  const subTotal = first.saleSubTotal || first.SaleSubTotal || totalAmt;
  const shipping = first.saleShippingTotal || first.SaleShippingTotal || 0;
  const lrAdjustmentAmount = Number(first.lrAdjustmentAmount || first.LrAdjustmentAmount || 0);
  const additionalChargesLableName = first.additionalChargesLableName || first.AdditionalChargesLableName || "Adjustment";
  const additionalChargesLableValue = Number(first.additionalChargesLableValue || first.AdditionalChargesLableValue || 0);
  const taxType = taxList[0]?.taxTypeId || 1;
  const isIntraState = Number(taxType) === 1;
  const isInterState = Number(taxType) === 2;
  const totalTax = isIntraState ? totalCGST + totalSGST : totalIGST;
  const netTotal = first.saleFinalGrandTotal || first.SaleFinalGrandTotal || (subTotal + shipping + totalTax + additionalChargesLableValue);
  const roundOff = first.roundOff;
  const upiId = header.upiId || "";
  const upiName = encodeURIComponent(companyInfo.consumerName || companyInfo.ConsumerName || "Company");
  const upiUrl = `upi://pay?pa=${upiId}&pn=${upiName}&am=${netTotal.toFixed(2)}&cu=INR`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(upiUrl)}`;

  // ── print ────────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const orig = document.title;
    document.title = `${header.saleNumber || header.SaleNumber || "invoice"}${(header.buyerName || header.BuyerName) ? " - " + (header.buyerName || header.BuyerName) : ""}`;
    window.print();
    setTimeout(() => { document.title = orig; }, 1200);
  };

  // ── PDF download via html2canvas + jsPDF ─────────────────────────────────────
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const loadScript = (src: string) =>
        new Promise<void>((res, rej) => {
          if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
          const s = document.createElement("script");
          s.src = src; s.onload = () => res(); s.onerror = () => rej();
          document.head.appendChild(s);
        });
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      const el = document.getElementById("transport-invoice-print-node");
      if (!el) throw new Error("not found");

      el.style.position = "fixed";
      el.style.top = "-9999px";
      el.style.left = "0";
      el.style.display = "block";
      el.style.width = "794px";
      await new Promise(r => setTimeout(r, 150));

      const canvas = await (window as any).html2canvas(el, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false,
        width: el.scrollWidth, height: el.scrollHeight,
      });

      el.style.position = ""; el.style.top = ""; el.style.left = "";
      el.style.display = "none"; el.style.width = "";

      const { jsPDF } = (window as any).jspdf;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210, ph = 297;
      const ih = (canvas.height * pw) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      let left = ih, pos = 0;
      pdf.addImage(imgData, "JPEG", 0, pos, pw, ih);
      left -= ph;
      while (left > 0) { pos = left - ih; pdf.addPage(); pdf.addImage(imgData, "JPEG", 0, pos, pw, ih); left -= ph; }

      const fn = `${header.saleNumber || header.SaleNumber || "invoice"}${(header.buyerName || header.BuyerName) ? " - " + (header.buyerName || header.BuyerName) : ""}.pdf`;
      pdf.save(fn);
    } catch (e) {
      console.error(e);
      handlePrint();
    } finally {
      setIsDownloading(false);
    }
  };

  // ── print + screen CSS ───────────────────────────────────────────────────────
  const printCSS = [
    ".tiv-root .MuiTypography-root { font-size: inherit !important; }",
    ".tiv-root td, .tiv-root th { font-size: inherit !important; }",
    "@media print {",
    "  @page { size: A4 portrait; margin: 0mm; }",
    "  html, body {",
    "    height: auto !important;",
    "    min-height: auto !important;",
    "    max-height: none !important;",
    "    overflow: visible !important;",
    "    position: static !important;",
    "  }",
    "  body > *:not(.MuiDialog-root) { display: none !important; }",
    "  .MuiBackdrop-root, .no-print, .no-print * { display: none !important; }",
    "  .MuiDialog-root, .MuiDialog-container, .MuiDialog-paper, .MuiDialogContent-root {",
    "    display: block !important; position: static !important;",
    "    width: 100% !important; max-width: 100% !important;",
    "    height: auto !important; min-height: auto !important; max-height: none !important;",
    "    margin: 0 !important; padding: 0 !important;",
    "    box-shadow: none !important; border: none !important;",
    "    overflow: visible !important; background: transparent !important;",
    "  }",
    "  #transport-invoice-print-node {",
    "    display: block !important; width: 100% !important; max-width: 100% !important;",
    "    height: auto !important; min-height: auto !important; max-height: none !important;",
    "    position: static !important; overflow: visible !important;",
    "    transform: none !important; padding: 0 !important; margin: 0 !important;",
    "    box-shadow: none !important; font-size: 8pt !important;",
    "  }",
    "  #transport-invoice-print-node td, #transport-invoice-print-node th {",
    "    white-space: normal !important; word-break: break-word !important;",
    "    overflow: visible !important; font-size: inherit !important;",
    "  }",
    "  #transport-invoice-preview-node { display: none !important; }",
    "  html, body, div, span, p { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }",
    "}",
  ].join("\n");


  // ── renderInvoice: pure HTML + inline styles — zero MUI inside ───────────────
  const renderInvoice = (nodeId: string, compact: boolean) => {
    // S = size helper. compact=true → pt (for print), false → px (for screen)
    // These map "logical label" to actual rendered size.
    const S = (px: number) => compact ? `${(px * 0.75).toFixed(1)}pt` : `${px}px`;

    const activeTemplate = template || "TransportInvoice";

    // Dynamic Style variables (standard professional gray/black theme)
    let primaryColor = "#000000";
    let accentColor = "#555555";
    let headerBg = "#f0f0f0";
    let borderStyle = "1px solid #cccccc";
    let tableHeaderBg = "#f0f0f0";
    let tableHeaderTextColor = "#000000";

    const BORDER = borderStyle;
    const ROOT: React.CSSProperties = {
      fontFamily: "Arial, sans-serif",
      fontSize: S(10),
      color: "#000",
      backgroundColor: "#fff",
      padding: compact ? "10mm 10mm 3mm 10mm" : "10mm 10mm 15mm 10mm",
      boxSizing: "border-box",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    };
    const TH: React.CSSProperties = {
      fontSize: S(9),
      fontWeight: "bold",
      padding: "3px 4px",
      borderRight: BORDER,
      borderBottom: BORDER,
      backgroundColor: tableHeaderBg,
      color: tableHeaderTextColor,
      textAlign: "left",
      lineHeight: 1.3,
      verticalAlign: "top",
      whiteSpace: "normal",
      wordBreak: "break-word",
    };
    const TD: React.CSSProperties = {
      fontSize: S(9),
      padding: "3px 4px",
      borderRight: BORDER,
      borderBottom: BORDER,
      lineHeight: 1.3,
      verticalAlign: "top",
      whiteSpace: "normal",
      wordBreak: "break-word",
    };

    const fmt2 = (n: number) => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmt3 = (n: number) => Number(n).toFixed(3);

    const logoImage = companyInfo.logoServerImage || "";
    const branchUdyam = companyInfo.branchUdyamNumber || companyInfo.BranchUdyamNumber || header.branchUdyamNumber || header.BranchUdyamNumber || "";
    const branchTax = companyInfo.branchTaxNumber || companyInfo.BranchTaxNumber || header.branchTaxNumber || header.BranchTaxNumber || "";
    const buyerStateCode = header.buyerStateCode || header.BuyerStateCode || "";
    const buyerState = header.buyerState || header.BuyerState || "";
    const taxCode = taxList[0]?.taxCode || taxList[0]?.TaxCode || "";
    const bankName = header.bankName || "";
    const bankIfscCode = header.bankIfscCode || "";
    const bankBranchName = header.bankBranchName || "";
    const bankAddress = header.bankAddress || "";
    const accountHolderName = header.accountHolderName || "";
    const accountName = header.accountName || "";

    // Chunking logic for dynamic pagination (hiding header on page 2+ increases capacity)
    const pages: any[][] = [];
    let tempDetails = [...details];
    let isFirst = true;
    while (tempDetails.length > 0) {
      if (isFirst) {
        if (tempDetails.length <= 12) {
          pages.push(tempDetails);
          break;
        } else {
          pages.push(tempDetails.slice(0, 18));
          tempDetails = tempDetails.slice(18);
          isFirst = false;
        }
      } else {
        if (tempDetails.length <= 16) {
          pages.push(tempDetails);
          break;
        } else {
          pages.push(tempDetails.slice(0, 24));
          tempDetails = tempDetails.slice(24);
        }
      }
    }
    if (pages.length === 0) {
      pages.push([]);
    }

    const copyTypes = selectedCopy === "Both"
      ? ["Original Copy", "Duplicate Copy"]
      : selectedCopy === "Original"
        ? ["Original Copy"]
        : ["Duplicate Copy"];

    return (
      <div id={nodeId} style={{ display: "flex", flexDirection: "column", gap: compact ? "0" : "20px" }}>
        {copyTypes.flatMap((copyType) =>
          pages.map((pageDetails, pageIdx) => {
            const isLastPage = pageIdx === pages.length - 1;
            const pageNo = pageIdx + 1;
            const totalPages = pages.length;

            const waitingRowsCount = pageDetails.filter((item: any) => Number(item.waitingCharges || item.WaitingCharges || 0) > 0).length;
            const targetRowCount = pageIdx === 0
              ? (isLastPage ? 9 : 18)
              : (isLastPage ? 15 : 24);
            const emptyRowsCount = Math.max(targetRowCount - pageDetails.length - waitingRowsCount, 1);

            return (
              <div key={`${copyType}-${pageIdx}`} style={{
                ...ROOT,
                height: compact ? "297mm" : "1120px",
                position: "relative",
                ...(compact ? {
                  pageBreakAfter: "always",
                  breakAfter: "page",
                } : {
                  minHeight: "1120px",
                  boxShadow: "0px 6px 18px rgba(0,0,0,0.38)",
                })
              }}>

                {/* ── Copy Type Label ── */}
                {pageIdx === 0 && (
                  <div style={{
                    position: "absolute",
                    top: compact ? "10mm" : "38px",
                    ...(activeTemplate === "TransportInvoice_2" ? {
                      left: compact ? "10mm" : "38px"
                    } : {
                      right: compact ? "10mm" : "38px"
                    }),
                    fontSize: S(8.5),
                    fontWeight: "bold",
                    padding: "2px 6px",
                    borderRadius: "2px",
                    textTransform: "uppercase",
                    color: primaryColor,
                    lineHeight: 1,
                  }}>
                    {copyType}
                  </div>
                )}

                {/* ── Title ── */}
                {pageIdx === 0 && (
                  <div style={{ borderBottom: `2px solid ${primaryColor}`, paddingBottom: "4px", marginBottom: "8px", textAlign: activeTemplate === "TransportInvoice_2" ? "right" : activeTemplate === "TransportInvoice_4" ? "left" : "center" }}>
                    <span style={{ fontWeight: "bold", fontSize: S(16), letterSpacing: "1px", color: primaryColor }}>
                      {activeTemplate === "TransportInvoice_5" ? "TAX INVOICE" : "TRANSPORT INVOICE"}
                    </span>
                  </div>
                )}

                {/* ── Company header ── */}
                {pageIdx === 0 && (
                  activeTemplate === "TransportInvoice_2" ? (
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", justifyContent: "flex-end", textAlign: "right", width: "100%", boxSizing: "border-box", marginBottom: "8px" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold", fontSize: S(18), marginBottom: "2px", color: primaryColor }}>
                          {companyInfo.firmName || ""}
                        </div>
                        <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}>
                          {[companyInfo.addressPart1 || companyInfo.AddressPart1,
                          companyInfo.addressPart2 || companyInfo.AddressPart2].filter(Boolean).join(", ")}
                        </div>
                        {branchTax && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>GSTIN:</b> {branchTax}</div>
                        )}
                        {branchUdyam && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>Udyam No:</b> {branchUdyam}</div>
                        )}
                        <div style={{ fontSize: S(9.5) }}>
                          <b>Contact:</b> {companyInfo.contactInformation || companyInfo.ContactInformation || "N/A"}
                        </div>
                      </div>
                      {logoImage && (
                        <img src={`data:image/png;base64,${logoImage}`} alt="logo"
                          style={{ width: compact ? 55 : 72, height: compact ? 55 : 72, objectFit: "contain", flexShrink: 0 }} />
                      )}
                    </div>
                  ) : activeTemplate === "TransportInvoice_3" ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: "12px", gap: "6px", width: "100%", boxSizing: "border-box" }}>
                      {logoImage && (
                        <img src={`data:image/png;base64,${logoImage}`} alt="logo"
                          style={{ width: compact ? 60 : 80, height: compact ? 60 : 80, objectFit: "contain", marginBottom: "4px" }} />
                      )}
                      <div>
                        <div style={{ fontWeight: "bold", fontSize: S(19), marginBottom: "2px", color: primaryColor }}>
                          {companyInfo.firmName || ""}
                        </div>
                        <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}>
                          {[companyInfo.addressPart1 || companyInfo.AddressPart1,
                          companyInfo.addressPart2 || companyInfo.AddressPart2].filter(Boolean).join(", ")}
                        </div>
                        {branchTax && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>GSTIN:</b> {branchTax}</div>
                        )}
                        {branchUdyam && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>Udyam No:</b> {branchUdyam}</div>
                        )}
                        <div style={{ fontSize: S(9.5), marginTop: "2px" }}>
                          <b>Contact:</b> {companyInfo.contactInformation || companyInfo.ContactInformation || "N/A"}
                        </div>
                      </div>
                    </div>
                  ) : activeTemplate === "TransportInvoice_4" ? (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", boxSizing: "border-box", marginBottom: "8px", borderBottom: BORDER, paddingBottom: "8px" }}>
                      {logoImage ? (
                        <img src={`data:image/png;base64,${logoImage}`} alt="logo"
                          style={{ width: compact ? 55 : 72, height: compact ? 55 : 72, objectFit: "contain", flexShrink: 0 }} />
                      ) : <div />}
                      <div style={{ textAlign: "right", flex: 1 }}>
                        <div style={{ fontWeight: "bold", fontSize: S(18), marginBottom: "2px", color: primaryColor }}>
                          {companyInfo.firmName || ""}
                        </div>
                        <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}>
                          {[companyInfo.addressPart1 || companyInfo.AddressPart1,
                          companyInfo.addressPart2 || companyInfo.AddressPart2].filter(Boolean).join(", ")}
                        </div>
                        {branchTax && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>GSTIN:</b> {branchTax}</div>
                        )}
                        {branchUdyam && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>Udyam No:</b> {branchUdyam}</div>
                        )}
                        <div style={{ fontSize: S(9.5) }}>
                          <b>Contact:</b> {companyInfo.contactInformation || companyInfo.ContactInformation || "N/A"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Default: TransportInvoice */
                    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "8px", width: "100%" }}>
                      {logoImage && (
                        <img src={`data:image/png;base64,${logoImage}`} alt="logo"
                          style={{ width: compact ? 55 : 72, height: compact ? 55 : 72, objectFit: "contain", flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold", fontSize: S(18), marginBottom: "2px" }}>
                          {companyInfo.firmName || ""}
                        </div>
                        <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}>
                          {[companyInfo.addressPart1 || companyInfo.AddressPart1,
                          companyInfo.addressPart2 || companyInfo.AddressPart2].filter(Boolean).join(", ")}
                        </div>
                        {branchTax && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>GSTIN:</b> {branchTax}</div>
                        )}
                        {branchUdyam && (
                          <div style={{ fontSize: S(9.5), lineHeight: 1.4 }}><b>Udyam No:</b> {branchUdyam}</div>
                        )}
                        <div style={{ fontSize: S(9.5) }}>
                          <b>Contact:</b> {companyInfo.contactInformation || companyInfo.ContactInformation || "N/A"}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* ── Bill To & Meta Row ── */}
                {pageIdx === 0 && (
                  activeTemplate === "TransportInvoice_2" ? (
                    <div style={{ marginBottom: "8px" }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: BORDER, paddingBottom: "2px", marginBottom: "6px", color: accentColor }}>
                        BILL TO &amp; INVOICE DETAILS
                      </div>
                      <div style={{ display: "flex" }}>
                        {/* Invoice meta on Left */}
                        <div style={{ flex: "0 0 42%", paddingRight: "12px" }}>
                          {[
                            { l: "Invoice #:", v: header.saleNumber || header.SaleNumber || "N/A", bold: true },
                            { l: "Date:", v: invoiceDate || "N/A" },
                            ...(dueDate ? [{ l: "Due On Date:", v: dueDate }] : []),
                            ...(companyInfo.financialYear ? [{ l: "Financial Year:", v: companyInfo.financialYear }] : []),
                            ...(taxCode ? [{ l: "HSN/SAC:", v: taxCode }] : []),
                          ].map(r => (
                            <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: S(9.5) }}>
                              <b style={{ whiteSpace: "nowrap", color: "#333" }}>{r.l}</b>
                              <span style={{ fontWeight: (r as any).bold ? "bold" : "normal", textAlign: "right" }}>{r.v}</span>
                            </div>
                          ))}
                        </div>
                        {/* Buyer on Right */}
                        <div style={{ flex: "0 0 58%", borderLeft: BORDER, paddingLeft: "12px" }}>
                          <div style={{ fontWeight: "bold", fontSize: S(11), marginBottom: "2px", color: primaryColor }}>
                            {header.buyerName || header.BuyerName || "N/A"}
                          </div>
                          {[header.buyerAddress1 || header.BuyerAddress1,
                          header.buyerAddress2 || header.BuyerAddress2,
                          [header.buyerCity || header.BuyerCity,
                          header.buyerState || header.BuyerState,
                          header.buyerPostalCode || header.BuyerPostalCode].filter(Boolean).join(", "),
                          header.buyerCountry || header.BuyerCountry]
                            .filter(Boolean).map((line: string, i: number) => (
                              <div key={i} style={{ fontSize: S(9.5), lineHeight: 1.35 }}>{line}</div>
                            ))}
                          <div style={{ display: "flex", gap: "16px", fontSize: S(9), marginTop: "4px", flexWrap: "wrap" }}>
                            {(header.taxNumber || header.TaxNumber) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <b style={{ color: "#333" }}>GSTIN:</b>
                                <b>{header.taxNumber || header.TaxNumber}</b>
                              </div>
                            )}
                            {(buyerStateCode || buyerState) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <b style={{ color: "#333" }}>Place of Supply:</b>
                                <span>{[buyerStateCode, buyerState].filter(Boolean).join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : activeTemplate === "TransportInvoice_3" ? (
                    <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
                      {/* Boxed Buyer Card */}
                      <div style={{ flex: "1", border: BORDER, borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ backgroundColor: headerBg, padding: "4px 8px", fontWeight: "bold", fontSize: S(9), color: primaryColor, borderBottom: BORDER }}>
                          BILL TO
                        </div>
                        <div style={{ padding: "8px" }}>
                          <div style={{ fontWeight: "bold", fontSize: S(11), marginBottom: "2px" }}>
                            {header.buyerName || header.BuyerName || "N/A"}
                          </div>
                          {[header.buyerAddress1 || header.BuyerAddress1,
                          header.buyerAddress2 || header.BuyerAddress2,
                          [header.buyerCity || header.BuyerCity,
                          header.buyerState || header.BuyerState,
                          header.buyerPostalCode || header.BuyerPostalCode].filter(Boolean).join(", ")]
                            .filter(Boolean).map((line: string, i: number) => (
                              <div key={i} style={{ fontSize: S(9.5), lineHeight: 1.35 }}>{line}</div>
                            ))}
                          <div style={{ display: "flex", gap: "16px", fontSize: S(9), marginTop: "4px", flexWrap: "wrap" }}>
                            {(header.taxNumber || header.TaxNumber) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <span style={{ color: "#555" }}>GSTIN:</span>
                                <b>{header.taxNumber || header.TaxNumber}</b>
                              </div>
                            )}
                            {(buyerStateCode || buyerState) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <span style={{ color: "#555" }}>Place of Supply:</span>
                                <span>{[buyerStateCode, buyerState].filter(Boolean).join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Boxed Invoice Meta Card */}
                      <div style={{ flex: "1", border: BORDER, borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ backgroundColor: headerBg, padding: "4px 8px", fontWeight: "bold", fontSize: S(9), color: primaryColor, borderBottom: BORDER }}>
                          INVOICE DETAILS
                        </div>
                        <div style={{ padding: "8px" }}>
                          {[
                            { l: "Invoice #:", v: header.saleNumber || header.SaleNumber || "N/A", bold: true },
                            { l: "Date:", v: invoiceDate || "N/A" },
                            ...(dueDate ? [{ l: "Due On Date:", v: dueDate }] : []),
                            ...(companyInfo.financialYear ? [{ l: "Financial Year:", v: companyInfo.financialYear }] : []),
                            ...(taxCode ? [{ l: "HSN/SAC:", v: taxCode }] : []),
                          ].map(r => (
                            <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: S(9.5) }}>
                              <span style={{ color: "#555" }}>{r.l}</span>
                              <span style={{ fontWeight: (r as any).bold ? "bold" : "normal" }}>{r.v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : activeTemplate === "TransportInvoice_4" ? (
                    <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
                      <div style={{ flex: "0 0 60%" }}>
                        <div style={{ borderLeft: `3px solid ${accentColor}`, paddingLeft: "8px" }}>
                          <div style={{ fontSize: S(8.5), fontWeight: "bold", color: accentColor, textTransform: "uppercase", marginBottom: "2px" }}>BILL TO</div>
                          <div style={{ fontWeight: "bold", fontSize: S(11), marginBottom: "2px" }}>
                            {header.buyerName || header.BuyerName || "N/A"}
                          </div>
                          {[header.buyerAddress1 || header.BuyerAddress1,
                          header.buyerAddress2 || header.BuyerAddress2,
                          [header.buyerCity || header.BuyerCity,
                          header.buyerState || header.BuyerState,
                          header.buyerPostalCode || header.BuyerPostalCode].filter(Boolean).join(", ")]
                            .filter(Boolean).map((line: string, i: number) => (
                              <div key={i} style={{ fontSize: S(9.5), lineHeight: 1.35, color: "#333" }}>{line}</div>
                            ))}
                          <div style={{ display: "flex", gap: "16px", fontSize: S(9), marginTop: "4px", flexWrap: "wrap" }}>
                            {(header.taxNumber || header.TaxNumber) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <span style={{ color: "#555" }}>GSTIN:</span>
                                <b>{header.taxNumber || header.TaxNumber}</b>
                              </div>
                            )}
                            {(buyerStateCode || buyerState) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <span style={{ color: "#555" }}>Place of Supply:</span>
                                <span>{[buyerStateCode, buyerState].filter(Boolean).join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ flex: "0 0 40%", borderLeft: BORDER, paddingLeft: "12px" }}>
                        <div style={{ fontSize: S(8.5), fontWeight: "bold", color: accentColor, textTransform: "uppercase", marginBottom: "4px" }}>DETAILS</div>
                        {[
                          { l: "Invoice #:", v: header.saleNumber || header.SaleNumber || "N/A", bold: true },
                          { l: "Date:", v: invoiceDate || "N/A" },
                          ...(dueDate ? [{ l: "Due On Date:", v: dueDate }] : []),
                          ...(companyInfo.financialYear ? [{ l: "Financial Year:", v: companyInfo.financialYear }] : []),
                          ...(taxCode ? [{ l: "HSN/SAC:", v: taxCode }] : []),
                        ].map(r => (
                          <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: S(9.5) }}>
                            <span style={{ color: "#555" }}>{r.l}</span>
                            <span style={{ fontWeight: (r as any).bold ? "bold" : "normal" }}>{r.v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* Default: TransportInvoice */
                    <div style={{ marginBottom: "8px" }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: BORDER, paddingBottom: "2px", marginBottom: "6px" }}>
                        BILL TO
                      </div>
                      <div style={{ display: "flex" }}>
                        {/* Buyer */}
                        <div style={{ flex: "0 0 58%", paddingRight: "10px" }}>
                          <div style={{ fontWeight: "bold", fontSize: S(11), marginBottom: "2px" }}>
                            {header.buyerName || header.BuyerName || "N/A"}
                          </div>
                          {[header.buyerAddress1 || header.BuyerAddress1,
                          header.buyerAddress2 || header.BuyerAddress2,
                          [header.buyerCity || header.BuyerCity,
                          header.buyerState || header.BuyerState,
                          header.buyerPostalCode || header.BuyerPostalCode].filter(Boolean).join(", "),
                          header.buyerCountry || header.BuyerCountry]
                            .filter(Boolean).map((line: string, i: number) => (
                              <div key={i} style={{ fontSize: S(9.5), lineHeight: 1.35 }}>{line}</div>
                            ))}
                          <div style={{ display: "flex", gap: "16px", fontSize: S(9), marginTop: "4px", flexWrap: "wrap" }}>
                            {(header.taxNumber || header.TaxNumber) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <b>GSTIN:</b>
                                <b>{header.taxNumber || header.TaxNumber}</b>
                              </div>
                            )}
                            {(buyerStateCode || buyerState) && (
                              <div style={{ display: "flex", gap: "4px" }}>
                                <b>Place of Supply:</b>
                                <span>{[buyerStateCode, buyerState].filter(Boolean).join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Invoice meta */}
                        <div style={{ flex: "0 0 42%", borderLeft: BORDER, paddingLeft: "12px" }}>
                          {[
                            { l: "Invoice #:", v: header.saleNumber || header.SaleNumber || "N/A", bold: true },
                            { l: "Date:", v: invoiceDate || "N/A" },
                            ...(dueDate ? [{ l: "Due On Date:", v: dueDate }] : []),
                            ...(companyInfo.financialYear ? [{ l: "Financial Year:", v: companyInfo.financialYear }] : []),
                            ...(taxCode ? [{ l: "HSN/SAC:", v: taxCode }] : []),
                          ].map(r => (
                            <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: S(9.5) }}>
                              <b style={{ whiteSpace: "nowrap" }}>{r.l}</b>
                              <span style={{ fontWeight: (r as any).bold ? "bold" : "normal", textAlign: "right" }}>{r.v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* ── Details Table ── */}
                <div style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", marginBottom: "8px", minHeight: 0 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", border: BORDER, height: "100%" }}>
                    {activeTemplate === "TransportInvoice_5" ? (
                      <colgroup>
                        <col style={{ width: "5%" }} />
                        <col style={{ width: "9%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "16%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "7%" }} />
                        <col style={{ width: "7%" }} />
                        <col style={{ width: "7%" }} />
                        <col style={{ width: "9%" }} />
                      </colgroup>
                    ) : (
                      <colgroup>
                        <col style={{ width: "5%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: "11%" }} />
                        <col style={{ width: "11%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "9%" }} />
                      </colgroup>
                    )}
                    <thead>
                      {activeTemplate === "TransportInvoice_5" ? (
                        <tr style={{ height: S(24) }}>
                          <th style={TH}>LR No</th>
                          <th style={TH}>LR Date
                            <div style={{ fontWeight: "normal", color: "#555", marginTop: "1px" }}>Invoice No</div>
                          </th>
                          <th style={TH}>Vehicle No</th>
                          <th style={TH}>Description</th>
                          <th style={TH}>
                            From Party
                            <div style={{ fontWeight: "normal", color: "#555", marginTop: "1px" }}>Bill From</div>
                          </th>
                          <th style={TH}>
                            To Party
                            <div style={{ fontWeight: "normal", color: "#555", marginTop: "1px" }}>Ship To</div>
                          </th>
                          <th style={{ ...TH, textAlign: "right" }}>Act. Qty</th>
                          <th style={{ ...TH, textAlign: "right" }}>Bill Qty</th>
                          <th style={{ ...TH, textAlign: "right" }}>Rate</th>
                          <th style={{ ...TH, textAlign: "right", borderRight: "none" }}>Amount (₹)</th>
                        </tr>
                      ) : (
                        <tr style={{ height: S(24) }}>
                          <th style={TH}>LR No</th>
                          <th style={TH}>LR Date
                          </th>
                          <th style={TH}>Vehicle No</th>
                          <th style={TH}>Description</th>
                          <th style={TH}>Bill From</th>
                          <th style={TH}>Ship To</th>
                          <th style={{ ...TH, textAlign: "right" }}>Act. Qty</th>
                          <th style={{ ...TH, textAlign: "right" }}>Bill Qty</th>
                          <th style={{ ...TH, textAlign: "right" }}>Rate</th>
                          <th style={{ ...TH, textAlign: "right", borderRight: "none" }}>Amount (₹)</th>
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      {pageDetails.length > 0 ? pageDetails.flatMap((item: any, idx: number) => {
                        const rows = [];
                        rows.push(
                          <tr key={`main-${idx}`} style={{ height: S(24) }}>
                            <td style={TD}>{item.lrNumber || item.LrNumber || "-"}</td>
                            <td style={TD}>
                              {activeTemplate === "TransportInvoice_5" ? (
                                <>
                                  <div>{fmtDate(item.lrDate || item.LrDate) || "-"}</div>
                                  {(item.invoiceRefNo || item.InvoiceRefNo) && (
                                    <div style={{ color: "#555", marginTop: "2px" }}>
                                      {item.invoiceRefNo || item.InvoiceRefNo}
                                    </div>
                                  )}
                                </>
                              ) : (
                                fmtDate(item.lrDate || item.LrDate) || "-"
                              )}
                            </td>
                            <td style={TD}>{item.vehicleNo || item.VehicleNo || "-"}</td>
                            <td style={TD}>{item.productName || item.ProductName || "-"}</td>
                            {activeTemplate === "TransportInvoice_5" ? (
                              <>
                                <td style={TD}>
                                  <div>{item.billFromPartyName || item.BillFromPartyName || "-"}</div>
                                  <div style={{ color: "#555", marginTop: "2px" }}>{item.billFrom || item.BillFrom || "-"}</div>
                                </td>
                                <td style={TD}>
                                  <div>{item.shipToPartyName || item.ShipToPartyName || "-"}</div>
                                  <div style={{ color: "#555", marginTop: "2px" }}>{item.shipTo || item.ShipTo || "-"}</div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={TD}>{item.billFrom || item.BillFrom || "-"}</td>
                                <td style={TD}>{item.shipTo || item.ShipTo || "-"}</td>
                              </>
                            )}
                            <td style={{ ...TD, textAlign: "right" }}>{fmt3(item.actualQuantity || item.ActualQuantity || 0)}</td>
                            <td style={{ ...TD, textAlign: "right" }}>{fmt3(item.billQuantity || item.BillQuantity || 0)}</td>
                            <td style={{ ...TD, textAlign: "right" }}>{fmt2(item.unitPrice || item.UnitPrice || 0)}</td>
                            <td style={{ ...TD, textAlign: "right", fontWeight: "bold", borderRight: "none" }}>
                              {fmt2(item.finalAmount || item.FinalAmount || 0)}
                            </td>
                          </tr>
                        );

                        if (Number(item.waitingCharges || item.WaitingCharges || 0) > 0) {
                          rows.push(
                            <tr key={`waiting-${idx}`} style={{ height: S(20) }}>
                              <td colSpan={9} style={{ ...TD, textAlign: "right", color: "#555", fontSize: S(8.5), borderBottom: BORDER }}>
                                <i>No. of days = {Number(item.numberOfDays || item.NumberOfDays || 0)} * per day Amount = {fmt2(item.perDayAmount || item.PerDayAmount || 0)} = Waiting Charges:</i>
                              </td>
                              <td style={{ ...TD, textAlign: "right", fontWeight: "bold",  fontSize: S(8.5), borderRight: "none", borderBottom: BORDER }}>
                                {fmt2(item.waitingCharges || item.WaitingCharges || 0)}
                              </td>
                            </tr>
                          );
                        }

                        return rows;
                      }) : (
                        <tr style={{ height: S(24) }}>
                          <td colSpan={10} style={{ ...TD, textAlign: "center", borderRight: "none" }}>
                            No Transport Details Available
                          </td>
                        </tr>
                      )}
                      {/* Empty rows to absorb table height stretch and draw vertical grid lines */}
                      {Array.from({ length: emptyRowsCount }).map((_, i) => (
                        <tr key={`empty-${i}`} style={{ height: "auto" }}>
                          <td style={{ ...TD, borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, textAlign: "right", borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, textAlign: "right", borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, textAlign: "right", borderBottom: "none" }}>&nbsp;</td>
                          <td style={{ ...TD, textAlign: "right", borderRight: "none", borderBottom: "none" }}>&nbsp;</td>
                        </tr>
                      ))}
                      {isLastPage && (
                        <tr style={{ backgroundColor: tableHeaderBg, height: S(24) }}>
                          <td colSpan={6} style={{ ...TH, textAlign: "right", borderTop: BORDER }}>Total:</td>
                          <td style={{ ...TH, textAlign: "right", borderTop: BORDER }}>{fmt3(totalActQty)}</td>
                          <td style={{ ...TH, textAlign: "right", borderTop: BORDER }}>{fmt3(totalBillQty)}</td>
                          <td style={{ ...TH, borderTop: BORDER }}></td>
                          <td style={{ ...TH, textAlign: "right", borderRight: "none", borderTop: BORDER }}>{fmt2(totalAmt)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── Tax Summary + Totals ── */}
                {isLastPage && (
                  <div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>
                    {/* Left: tax table */}
                    <div style={{ flex: "0 0 58%" }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold", textTransform: "uppercase", marginBottom: "3px", color: accentColor }}>Tax Summary</div>
                      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", border: BORDER }}>
                        <thead>
                          <tr>
                            <th style={TH}>Taxable Amt</th>
                            {isIntraState && <>
                              <th style={{ ...TH, textAlign: "right" }}>CGST</th>
                              <th style={{ ...TH, textAlign: "right" }}>SGST</th>
                            </>}
                            {isInterState && <th style={{ ...TH, textAlign: "right" }}>IGST</th>}
                            <th style={{ ...TH, textAlign: "right", borderRight: "none" }}>Tax Amt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxList.length > 0 ? taxList.map((item: any, idx: number) => {
                            const cA = Number(item.totalCentralTaxAmount || 0);
                            const sA = Number(item.totalStateTaxAmount || 0);
                            const iA = Number(item.totalIntegratedTaxAmount || 0);
                            const cP = Number(item.centralTax || 0);
                            const sP = Number(item.stateTax || 0);
                            const iP = Number(item.integratedTax || 0);
                            return (
                              <tr key={idx}>
                                <td style={TD}>{fmt2(item.totalTaxableAmount || 0)}</td>
                                {isIntraState && <>
                                  <td style={{ ...TD, textAlign: "right" }}>{cA > 0 ? `${cP}% (${fmt2(cA)})` : "-"}</td>
                                  <td style={{ ...TD, textAlign: "right" }}>{sA > 0 ? `${sP}% (${fmt2(sA)})` : "-"}</td>
                                </>}
                                {isInterState && <td style={{ ...TD, textAlign: "right" }}>{iA > 0 ? `${iP}% (${fmt2(iA)})` : "-"}</td>}
                                <td style={{ ...TD, textAlign: "right", fontWeight: "bold", borderRight: "none" }}>{fmt2(totalTax)}</td>
                              </tr>
                            );
                          }) : (
                            <tr><td colSpan={isIntraState ? 4 : 3} style={{ ...TD, textAlign: "center", borderRight: "none" }}>No Tax Summary</td></tr>
                          )}
                          <tr style={{ backgroundColor: tableHeaderBg }}>
                            <td style={TH}>{fmt2(totalTaxableA)}</td>
                            {isIntraState && <>
                              <td style={{ ...TH, textAlign: "right" }}>{fmt2(totalCGST)}</td>
                              <td style={{ ...TH, textAlign: "right" }}>{fmt2(totalSGST)}</td>
                            </>}
                            {isInterState && <td style={{ ...TH, textAlign: "right" }}>{fmt2(totalIGST)}</td>}
                            <td style={{ ...TH, textAlign: "right", borderRight: "none" }}>{fmt2(totalTax)}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div style={{ marginTop: "4px", fontSize: S(9) }}>
                        <div><b>Tax Amount (in words):</b> {convertNumberToWords(totalTax)}</div>
                        <div style={{ marginTop: "2px" }}><b>Net Amount (in words):</b> {convertNumberToWords(netTotal)}</div>
                      </div>
                    </div>

                    {/* Right: summary box */}
                    <div style={{ flex: "0 0 40%", border: BORDER, padding: "8px 10px", boxSizing: "border-box", borderRadius: activeTemplate === "TransportInvoice_3" ? "4px" : "0px" }}>
                      {[
                        { l: "Sub Total:", v: fmt2(subTotal) },
                        { l: "Shipping Charges:", v: fmt2(shipping) },
                        ...(Math.abs(lrAdjustmentAmount) > 0.01 || Math.abs(shipping) > 0.01 ? [{ l: "Taxable Amount:", v: fmt2(totalTaxableA || (subTotal + shipping)) }] : []),
                        { l: "GST Amount:", v: fmt2(totalTax) },
                        ...(Math.abs(additionalChargesLableValue) > 0.01 ? [{ l: `${additionalChargesLableName}:`, v: fmt2(additionalChargesLableValue) }] : []),
                        ...(Math.abs(roundOff) > 0.01 ? [{ l: "Round Off:", v: fmt2(roundOff) }] : []),
                      ].map(r => (
                        <div key={r.l} style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                          <span style={{ fontSize: S(9.5), color: "#555" }}>{r.l}</span>
                          <span style={{ fontSize: S(9.5) }}>{r.v}</span>
                        </div>
                      ))}
                      <div style={{ display: "flex", justifyContent: "space-between", borderTop: `2px solid ${primaryColor}`, paddingTop: "4px", marginTop: "4px" }}>
                        <span style={{ fontSize: S(11), fontWeight: "bold", color: primaryColor }}>Net Amount:</span>
                        <span style={{ fontSize: S(11), fontWeight: "bold", color: primaryColor }}>₹{fmt2(netTotal)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Footer ── */}
                {isLastPage && (
                  <div style={{ borderTop: BORDER, paddingTop: "6px", display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    {/* Notes */}
                    <div style={{ flex: "1 1 auto" }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold", color: accentColor, marginBottom: "2px" }}>Customer Notes</div>
                      <div style={{ fontSize: S(8.5), color: "#666", fontStyle: "italic", marginBottom: "3px" }}>
                        Thank you for your business. Please process this invoice within terms.
                      </div>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold" }}>
                        SUBJECT TO {companyInfo.branchName?.toUpperCase() || ""} JURISDICTION
                      </div>
                      <div style={{ fontSize: S(8), color: "#666", marginBottom: "4px" }}>E &amp; O.E</div>
                      <div style={{
                        borderTop: "1px dashed #ccc",
                        paddingTop: "4px",
                        fontSize: S(7.5),
                        lineHeight: 1.35,
                        color: "#444",
                        textAlign: "justify"
                      }}>
                        <b>DECLARATION:</b> We have taken registration under the CGST Act, 2017 and have exercised the option to pay tax on services of GTA in relation to transport of goods supplied by us during the Financial Year 2026-27 under forward charge.
                      </div>
                    </div>
                    {/* QR */}
                    <div style={{ flex: "0 0 14%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold" }}>UPI: {upiId}</div>
                      <img src={qrUrl} alt="QR" style={{ width: 55, height: 55, border: BORDER, borderRadius: activeTemplate === "TransportInvoice_3" ? "4px" : "0px" }} />
                      <div style={{ fontSize: S(8), color: "#666" }}>Scan to Pay</div>
                    </div>
                    {/* Bank Details */}
                    <div style={{ flex: "0 0 24%", fontSize: S(8), lineHeight: 1.35 }}>
                      <div style={{ fontSize: S(8.5), fontWeight: "bold", color: accentColor, marginBottom: "3px", textTransform: "uppercase" }}>Bank Details</div>
                      {accountName && <div><b>A/C No:</b> {accountName}</div>}
                      {accountHolderName && <div><b>Holder:</b> {accountHolderName}</div>}
                      {bankName && <div><b>Bank:</b> {bankName}</div>}
                      {bankIfscCode && <div><b>IFSC:</b> {bankIfscCode}</div>}
                      {bankBranchName && <div><b>Branch:</b> {bankBranchName}</div>}
                      {bankAddress && <div style={{ fontSize: S(7.5), color: "#555" }}>{bankAddress}</div>}
                    </div>
                    {/* Signature */}
                    <div style={{ flex: "0 0 20%", textAlign: "right", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: "90px", alignItems: "flex-end" }}>
                      <div style={{ fontSize: S(10.5), fontWeight: "bold", color: primaryColor }}>
                        For {companyInfo.firmName || companyInfo.FirmName || companyInfo.consumerName || companyInfo.ConsumerName || ""}
                      </div>
                      {(companyInfo.signatureServerImage || companyInfo.SignatureServerImage) && (
                        <img src={`data:image/png;base64,${companyInfo.signatureServerImage || companyInfo.SignatureServerImage}`}
                          alt="Signature" style={{ height: 40, maxWidth: 100, objectFit: "contain", margin: "4px 0" }} />
                      )}
                      <div style={{ fontSize: S(9), color: "#555", fontStyle: "italic", borderTop: "1px dashed #000", paddingTop: "3px", width: "100px", textAlign: "center" }}>
                        Authorised Signature
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Page Footer for Intermediate Pages ── */}
                {!isLastPage && (
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: BORDER,
                    paddingTop: "6px",
                    fontSize: S(8.5),
                    color: "#666",
                    marginTop: "auto"
                  }}>
                    <span>Continued...</span>
                    <span>Page {pageNo} of {totalPages}</span>
                  </div>
                )}

              </div>
            );
          })
        )}
      </div>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: printCSS }} />

      {/* Hidden print node */}
      <div style={{ display: "none" }} id="transport-invoice-print-node">
        {renderInvoice("transport-invoice-print-inner", true)}
      </div>

      {isMobile ? (
        <Box
          className="no-print"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: "#f8f9fa",
          }}
        >
          {/* Mobile Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #dfe1e5",
              bgcolor: "#fff",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#202124" }}>
              Invoice Actions
            </Typography>
            <IconButton onClick={onClose} size="medium" sx={{ color: "grey.700" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Action Area */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              p: 3,
              gap: 2.5,
              overflowY: "auto",
            }}
          >
            {/* Invoice Info */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1a73e8", mb: 0.5 }}>
                {header.saleNumber || header.SaleNumber || "Invoice"}
              </Typography>
              {(header.buyerName || header.BuyerName) && (
                <Typography variant="body2" sx={{ color: "grey.600" }}>
                  {header.buyerName || header.BuyerName}
                </Typography>
              )}
              {netTotal !== undefined && (
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#202124", mt: 1 }}>
                  ₹{Number(netTotal).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              )}
            </Box>

            {/* Select Copy option for mobile as well (helpful if they want duplicate vs original copy downloaded/printed) */}
            <Box sx={{ width: "100%", maxWidth: "320px", display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="caption" sx={{ color: "grey.600", fontWeight: "bold" }}>
                Select Invoice Copy
              </Typography>
              <Select
                value={selectedCopy}
                onChange={(e) => setSelectedCopy(e.target.value as string)}
                size="small"
                fullWidth
                sx={{
                  bgcolor: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dadce0" }
                }}
              >
                <MenuItem value="Both">Both Copies</MenuItem>
                <MenuItem value="Original">Original Copy</MenuItem>
                <MenuItem value="Duplicate">Duplicate Copy</MenuItem>
              </Select>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ width: "100%", maxWidth: "320px", display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {/* Download Button */}
              <Button
                variant="contained"
                onClick={handleDownloadPDF}
                disabled={isDownloading}
                startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: "8px",
                  bgcolor: "#1a73e8",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "15px",
                  boxShadow: "0 2px 4px rgba(26,115,232,0.2)",
                  "&:hover": { bgcolor: "#1557b0" }
                }}
              >
                {isDownloading ? "Downloading PDF..." : "Download PDF"}
              </Button>

              {/* Print Button */}
              <Button
                variant="outlined"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: "8px",
                  borderColor: "#dadce0",
                  color: "#3c4043",
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "15px",
                  bgcolor: "#fff",
                  "&:hover": {
                    borderColor: "#bdc1c6",
                    bgcolor: "#f8f9fa",
                  }
                }}
              >
                Print Invoice
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* ── Toolbar ── */}
          <Box
            className="no-print"
            sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              px: 2, py: 1, borderBottom: "1px solid #dfe1e5", bgcolor: "#f8f9fa",
              height: "56px", flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton size="small" sx={{ color: "grey.700" }}><MenuOpenIcon /></IconButton>
              <Box sx={{ display: "flex", alignItems: "center", borderLeft: "1px solid #dadce0", pl: 1.5, gap: 1.5 }}>
                <Select
                  value={selectedCopy}
                  onChange={(e) => setSelectedCopy(e.target.value as string)}
                  size="small"
                  sx={{ height: "32px", fontSize: "13px", bgcolor: "#fff", minWidth: "125px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dadce0" } }}
                >
                  <MenuItem value="Both">Both Copies</MenuItem>
                  <MenuItem value="Original">Original Copy</MenuItem>
                  <MenuItem value="Duplicate">Duplicate Copy</MenuItem>
                </Select>
                <Select
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  size="small"
                  sx={{ height: "32px", fontSize: "13px", bgcolor: "#fff", minWidth: "85px", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dadce0" } }}
                >
                  {[50, 75, 100, 125, 150, 175, 200].map(v => <MenuItem key={v} value={v}>{v}%</MenuItem>)}
                </Select>
                <IconButton size="small" onClick={() => setZoom(p => Math.max(p - 10, 50))} disabled={zoom <= 50} sx={{ border: "1px solid #dadce0", bgcolor: "#fff" }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setZoom(p => Math.min(p + 10, 200))} disabled={zoom >= 200} sx={{ border: "1px solid #dadce0", bgcolor: "#fff" }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton onClick={handlePrint} size="medium" sx={{ color: "grey.700" }} title="Print"><PrintIcon /></IconButton>
              <IconButton onClick={handleDownloadPDF} size="medium" sx={{ color: "grey.700" }} title="Download PDF" disabled={isDownloading}>
                {isDownloading ? <CircularProgress size={20} /> : <GetAppIcon />}
              </IconButton>
              <Box sx={{ borderLeft: "1px solid #dadce0", ml: 1, pl: 1 }}>
                <IconButton onClick={onClose} size="medium" sx={{ color: "grey.700" }}><CloseIcon /></IconButton>
              </Box>
            </Box>
          </Box>

          {/* ── Document Canvas ── */}
          <DialogContent
            className="no-print"
            sx={{
              p: 0, bgcolor: "#525659",
              display: "flex", justifyContent: "center",
              overflowY: "auto", overflowX: "hidden",
              flex: 1, alignItems: "flex-start",
            }}
          >
            <Box sx={{ py: 3, px: 2, width: "100%", display: "flex", justifyContent: "center", boxSizing: "border-box" }}>
              <Box
                id="transport-invoice-preview-node"
                sx={{
                  width: "min(100%, 794px)",
                  flexShrink: 0,
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: "top center",
                  transition: "transform 0.12s ease-out",
                  mb: `${((zoom / 100) - 1) * 100}%`,
                }}
              >
                {renderInvoice("transport-invoice-preview-inner", false)}
              </Box>
            </Box>
          </DialogContent>
        </>
      )}
    </>
  );
}